import { GroupType } from "authoring/lib/enum/group-type.mjs"
import { isAlreadyPublishedNewSchedule } from "authoring/lib/enum/new-schedule-creation-type.mjs"
import { getTaskAmendsKey } from "authoring/routes/project/amends/lib/task-amends-key"
import { hasAmendsContent } from "authoring/routes/project/amends/lib/amends-content"
import { normalisePlainText } from "authoring/routes/project/amends/lib/normalise-plain-text.mjs"
import { INTRO_CONTENT_AMENDS_KEY, INTRO_NOTE_AMENDS_KEY } from "../../amends/lib/constants/amends-keys"
import { areAllSchedulesNew } from "./are-all-schedules-new.mjs"

export function getMissingGroupAmends(group = {}, options = {}) {
    if (group?.type === GroupType.ADD) return getMissingAddGroupAmends(group, options)
    if (group?.type === GroupType.INTRODUCTION) return getMissingIntroductionGroupAmends(group, options)
    return []
}

// Gates the new-schedule opt-out dialog: no point asking whether to record a note that was
// never written.
export function hasAnyGroupAmendsContent(group = {}) {
    if (group?.type === GroupType.ADD) return hasAnyAddGroupAmendsContent(group)
    if (group?.type === GroupType.INTRODUCTION) return hasAnyIntroductionGroupAmendsContent(group)
    return false
}

function hasAnyAddGroupAmendsContent(group = {}) {
    const { newTasks = [], amends = {} } = group
    return newTasks.some((task) => {
        if (task?.isHeader) return false
        const amendsKey = getTaskAmendsKey(task)
        return hasAmendsContent(amends?.[amendsKey]?.payload?.content)
    })
}

function hasAnyIntroductionGroupAmendsContent(group = {}) {
    const { amends = {} } = group
    return (
        hasAmendsContent(amends?.[INTRO_CONTENT_AMENDS_KEY]?.payload?.content) ||
        hasAmendsContent(amends?.[INTRO_NOTE_AMENDS_KEY]?.payload?.content)
    )
}

function getMissingAddGroupAmends(group = {}, options = {}) {
    const eligibleSchedules = excludeAlreadyPublishedSchedules(group?.addTypeSchedules, options?.freshSchedulesById)

    // Notes justify a change against existing published content, so they stay author's-choice
    // when every current target is a schedule that has none yet.
    if (areAllSchedulesNew(eligibleSchedules)) return []

    const { newTasks = [], amends = {} } = group
    const output = []

    for (const [index, task] of newTasks.entries()) {
        if (task?.isHeader) continue

        const amendsKey = getTaskAmendsKey(task)
        const content = amends?.[amendsKey]?.payload?.content
        if (hasAmendsContent(content)) continue

        output.push(getAddTaskLabel(task, index))
    }

    return output
}

function getAddTaskLabel(task = {}, index = 0) {
    const title = normalisePlainText(task?.title)
    if (title) return `Task ${index + 1}: ${title}`
    return `Task ${index + 1}`
}

function getMissingIntroductionGroupAmends(group = {}, options = {}) {
    const eligibleSchedules = excludeAlreadyPublishedSchedules(group?.introTypeSchedules, options?.freshSchedulesById)
    if (areAllSchedulesNew(eligibleSchedules)) return []

    const { amends = {}, intro = {} } = group
    const output = []

    if (hasAmendsContent(intro?.content) && !hasAmendsContent(amends?.[INTRO_CONTENT_AMENDS_KEY]?.payload?.content)) {
        output.push("Introduction")
    }

    if (hasAmendsContent(intro?.note) && !hasAmendsContent(amends?.[INTRO_NOTE_AMENDS_KEY]?.payload?.content)) {
        output.push("Notes")
    }

    return output
}

function excludeAlreadyPublishedSchedules(schedules = [], freshSchedulesById = {}) {
    return schedules.filter((schedule) => !isAlreadyPublishedNewSchedule(freshSchedulesById?.[schedule?.id]))
}
